import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../services/api';

function ChangeOrderDetail() {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchOrder();
  }, [orderId]);

  const fetchOrder = async () => {
    setLoading(true);
    try {
      const data = await api.getChangeOrder(orderId);
      setOrder(data);
    } catch (err) {
      setError(err.message || 'Failed to load change order');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount, currency = '$') => {
    if (!amount) return `${currency}0.00`;
    return `${currency}${parseFloat(amount).toFixed(2)}`;
  };

  const getStatusBadge = (status) => {
    const statusStyles = {
      draft: 'bg-slate-100 text-slate-700',
      sent: 'bg-blue-100 text-blue-800',
      signed: 'bg-green-100 text-green-800',
    };
    return statusStyles[status] || 'bg-slate-100 text-slate-700';
  };

  const calculateTotal = () => {
    if (!order?.line_items?.length) return parseFloat(order?.total || 0);
    return order.line_items.reduce((sum, item) => {
      if (item.item_type === 'labor' || item.item_type === 'hourly') {
        return sum + (parseFloat(item.hours || 0) * parseFloat(item.rate || 0));
      }
      return sum + parseFloat(item.cost || 0);
    }, 0);
  };

  if (loading) {
    return (
      <div className="py-20 px-6 bg-background min-h-screen flex items-center justify-center">
        <div className="text-center">
          <i className="fas fa-spinner fa-spin text-4xl text-primary mb-4"></i>
          <p className="text-slate-600">Loading change order...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-20 px-6 bg-background min-h-screen">
        <div className="max-w-4xl mx-auto">
          <div className="bg-red-50 border border-red-200 text-red-700 p-6 rounded-xl text-center">
            <i className="fas fa-exclamation-circle text-3xl mb-3"></i>
            <p>{error}</p>
            <Link
              to="/change-orders"
              className="mt-4 inline-block text-primary hover:underline"
            >
              Back to Change Orders
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="py-20 px-6 bg-background min-h-screen">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-slate-600">Change order not found</p>
          <Link to="/change-orders" className="text-primary hover:underline">
            Back to Change Orders
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="py-12 px-6 bg-background min-h-screen">
      <div className="max-w-4xl mx-auto">
        {/* Back Link */}
        <Link
          to="/change-orders"
          className="inline-flex items-center text-slate-600 hover:text-primary mb-6 transition"
        >
          <i className="fas fa-arrow-left mr-2"></i>
          Back to Change Orders
        </Link>

        {/* Header */}
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200 mb-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-black text-primary mb-2">
                {order.title || 'Change Order'}
              </h1>
              <span className={`inline-block px-3 py-1 text-sm font-semibold rounded-full ${getStatusBadge(order.status)}`}>
                {order.status}
              </span>
            </div>
            <div className="text-right">
              <p className="text-sm text-slate-500">Total Amount</p>
              <p className="text-3xl font-bold text-primary">
                {formatCurrency(order.total || calculateTotal())}
              </p>
            </div>
          </div>

          {/* Order Meta */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t border-slate-200">
            {order.client_name && (
              <div>
                <p className="text-sm text-slate-500 mb-1">Client</p>
                <p className="font-medium text-slate-800">
                  <i className="fas fa-user mr-2 text-slate-400"></i>
                  {order.client_name}
                </p>
              </div>
            )}
            {order.job_title && (
              <div>
                <p className="text-sm text-slate-500 mb-1">Job</p>
                <p className="font-medium text-slate-800">
                  <i className="fas fa-briefcase mr-2 text-slate-400"></i>
                  {order.job && (
                    <Link to={`/jobs/${order.job}`} className="text-primary hover:underline">
                      {order.job_title}
                    </Link>
                  )}
                  {!order.job && order.job_title}
                </p>
              </div>
            )}
            <div>
              <p className="text-sm text-slate-500 mb-1">Created</p>
              <p className="font-medium text-slate-800">
                <i className="fas fa-calendar mr-2 text-slate-400"></i>
                {new Date(order.created_at).toLocaleDateString()}
              </p>
            </div>
          </div>
        </div>

        {/* Line Items */}
        {order.line_items && order.line_items.length > 0 && (
          <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200 mb-6">
            <h2 className="text-lg font-bold text-primary mb-4">Line Items</h2>
            <div className="space-y-3">
              {order.line_items.map((item, index) => {
                const isLabor = item.item_type === 'labor' || item.item_type === 'hourly';
                const itemTotal = isLabor
                  ? parseFloat(item.hours || 0) * parseFloat(item.rate || 0)
                  : parseFloat(item.cost || 0);

                return (
                  <div
                    key={item.id || index}
                    className="flex items-center justify-between py-3 border-b border-slate-100 last:border-0"
                  >
                    <div className="flex-1">
                      <p className="font-medium text-slate-800">{item.description}</p>
                      <p className="text-sm text-slate-500">
                        {isLabor ? (
                          <>{item.hours}h @ {formatCurrency(item.rate)}/hr</>
                        ) : (
                          <span className="capitalize">{item.item_type || 'Material'}</span>
                        )}
                      </p>
                    </div>
                    <p className="font-semibold text-slate-800">
                      {formatCurrency(itemTotal)}
                    </p>
                  </div>
                );
              })}
            </div>

            {/* Total */}
            <div className="flex items-center justify-between pt-4 mt-4 border-t-2 border-slate-200">
              <p className="text-lg font-bold text-slate-800">Total</p>
              <p className="text-xl font-bold text-primary">
                {formatCurrency(order.total || calculateTotal())}
              </p>
            </div>
          </div>
        )}

        {/* Signature */}
        {order.signature && (
          <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200 mb-6">
            <h2 className="text-lg font-bold text-primary mb-4">
              <i className="fas fa-signature mr-2"></i>
              Client Signature
            </h2>
            <div className="border border-slate-200 rounded-xl p-4 bg-slate-50">
              <img
                src={order.signature}
                alt="Client signature"
                className="max-h-32 mx-auto"
              />
            </div>
            {order.signed_at && (
              <p className="text-sm text-slate-500 text-center mt-2">
                Signed: {new Date(order.signed_at).toLocaleString()}
              </p>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200">
          <h2 className="text-lg font-bold text-primary mb-4">Actions</h2>
          <div className="flex flex-wrap gap-3">
            {order.job && (
              <Link
                to={`/jobs/${order.job}`}
                className="px-4 py-2 bg-primary text-white rounded-lg font-medium hover:bg-primary/90 transition"
              >
                <i className="fas fa-briefcase mr-2"></i>
                View Job
              </Link>
            )}
            {order.pdf_file && (
              <a
                href={order.pdf_file}
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-2 bg-secondary text-white rounded-lg font-medium hover:bg-secondary/90 transition"
              >
                <i className="fas fa-file-pdf mr-2"></i>
                View PDF
              </a>
            )}
          </div>
        </div>

        {/* Timestamps */}
        <div className="mt-6 text-center text-sm text-slate-500">
          {order.created_at && (
            <p>Created: {new Date(order.created_at).toLocaleString()}</p>
          )}
          {order.sent_at && (
            <p>Sent: {new Date(order.sent_at).toLocaleString()}</p>
          )}
        </div>
      </div>
    </div>
  );
}

export default ChangeOrderDetail;
